/**
 * Box pickers of the customer dialogs (old `walletOpts` App.tsx:47660-47713 and `WalletSelect` HelperComponents.tsx
 * :333-560), from the ledger read model (`GET /accounts`): IQD → the main vault (the operator's custody while sub-safes
 * are on, D-118; the engine routes the cash leg there) and the digital / cash boxes; USD → the dollar vault only
 * (D-029; the old list offered the main vault's IQD balance with a "$", R-5 / F-076).
 */
import type { SelectOption, WalletSelectOption } from "@faylasuf/ui";
import type { Translate } from "@faylasuf/i18n";
import type { AccountRow, SessionInfo } from "../../api/types.js";
import { fmt } from "./format.js";

export interface CustomerBoxes {
  readonly iqd: readonly WalletSelectOption[];
  readonly usd: readonly WalletSelectOption[];
  readonly iqdVaultId: string;
  readonly dollarVaultId: string;
}

export function customerBoxes(accounts: readonly AccountRow[], session: SessionInfo, subSafesActive: boolean, t: Translate): CustomerBoxes {
  const live = accounts.filter((a) => !a.archived);
  const vaultOf = (cur: string) => live.find((a) => a.kind === "cash_vault" && a.currency === cur);
  const custodyOf = (vaultId: string) => live.find((a) => a.kind === "custody" && a.parentAccountId === vaultId && a.ownerId === session.user.id);
  const vaultOption = (cur: "IQD" | "USD"): WalletSelectOption | null => {
    const v = vaultOf(cur);
    if (!v) return null;
    const unit = cur === "USD" ? "$" : t("unit.iqd");
    const custody = subSafesActive ? custodyOf(v.id) : undefined;
    if (custody) return { id: v.id, label: t("cust.sub_safe_label", { user: session.user.username }), balanceLabel: `${fmt(custody.available)} ${unit}` };
    return { id: v.id, label: cur === "USD" ? (v.name ?? t("cust.dollar_vault")) : (v.name ?? t("vault")), balanceLabel: `${fmt(v.available)} ${unit}` };
  };
  const iqdVault = vaultOption("IQD");
  const usdVault = vaultOption("USD");
  const boxes = live
    .filter((a) => (a.kind === "digital_box" || a.kind === "cash_box") && a.currency === "IQD")
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((a): WalletSelectOption => ({ id: a.id, label: a.name ?? a.kind, balanceLabel: `${fmt(a.available)} ${t("unit.iqd")}` }));
  return {
    iqd: [...(iqdVault ? [iqdVault] : []), ...boxes],
    usd: usdVault ? [usdVault] : [],
    iqdVaultId: iqdVault?.id ?? "",
    dollarVaultId: usdVault?.id ?? "",
  };
}

/** The add form's CustomSelect options (old `subLabel: "الرصيد: …"`). */
export function selectOptions(list: readonly WalletSelectOption[], t: Translate): SelectOption[] {
  return list.map((o) => ({ id: o.id, label: o.label, subLabel: t("cust.balance_sub", { amount: o.balanceLabel }) }));
}
